import { NgModule, Optional, SkipSelf } from "@angular/core";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireAnalyticsModule } from "@angular/fire/compat/analytics";
import { AngularFireAuthGuardModule } from "@angular/fire/compat/auth-guard";
import { AngularFirestore, AngularFirestoreModule } from "@angular/fire/compat/firestore";
import { AngularFireFunctionsModule, ORIGIN, REGION } from "@angular/fire/compat/functions";
import { AngularFireStorageModule } from "@angular/fire/compat/storage";
import { MatDialog } from "@angular/material/dialog";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import firebase from "firebase/compat/app";
import { environment } from "src/environments/environment";
import { FirebaseFactoryService } from "../services/firebase-factory.service";
import { AuthService } from "./auth.service";
import { IndexedDBRecoveryService } from "./indexeddb-recovery.service";
import { throwIfAlreadyLoaded } from "./module-import.guard";
import { PrivateBrowsingDetectorService } from "./private-browsing-detector.service";
import { FirebaseApp } from "firebase/app";

@NgModule({
  imports: [
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireFunctionsModule,
    AngularFireAuthGuardModule,
    AngularFirestoreModule,
    AngularFireAnalyticsModule,
    AngularFireStorageModule,
  ],
  providers: [
    { provide: REGION, useValue: AuthService.FIREBASE_REGION },
    { provide: ORIGIN, useValue: environment.emulateFirebase ? "http://localhost:5001" : undefined },
    FirebaseFactoryService,
  ],
})
export class FirebaseModule {
  constructor(
    @Optional() @SkipSelf() parentModule: FirebaseModule,
    private angularFirestore: AngularFirestore,
    private dialog: MatDialog,
    private firebaseFactory: FirebaseFactoryService,
    private indexedDBRecovery: IndexedDBRecoveryService,
    private privateBrowsingDetector: PrivateBrowsingDetectorService
  ) {
    // CRITICAL: Register global window.onerror / unhandledrejection listeners BEFORE
    // Firebase initialises so that the Firebase SDK's intentional IndexedDB corruption
    // error (thrown inside onupgradeneeded, outside Angular's zone) is intercepted and
    // handled by the recovery service instead of reaching Sentry as an unhandled error.
    this.indexedDBRecovery.registerGlobalErrorListeners();
    this.initializeFirebase(parentModule, angularFirestore);
  }

  private async initializeFirebase(
    parentModule: FirebaseModule,
    angularFirestore: AngularFirestore
  ) {

    try {

      const isPrivateBrowsing = await this.privateBrowsingDetector.detectPrivateBrowsing();

      if (isPrivateBrowsing) {
        this.setupMemoryOnlyMode(angularFirestore);
        throwIfAlreadyLoaded(parentModule, "CoreModule");
        return;
      }

      // CRITICAL: Test IndexedDB stability before initializing Firebase services
      // This prevents the iOS Safari race condition where createObjectStore fails
      const isIndexedDBStable = await this.privateBrowsingDetector.testIndexedDBStability();

      if (!isIndexedDBStable) {
        console.warn("[FIREBASE] INDEXEDDB-UNSTABLE - IndexedDB is not stable. Using memory-only mode.");
        this.setupMemoryOnlyMode(angularFirestore);
        throwIfAlreadyLoaded(parentModule, "CoreModule");
        return;
      }

      // Ensure the Firebase factory has initialized the Firebase app
      const firebaseApp = this.firebaseFactory.getFirebaseApp();

      // Initialize Firebase Analytics only if IndexedDB is stable
      // Analytics initialization can trigger IndexedDB operations that may fail
      await this.initializeAnalytics(firebaseApp);
      const firestore = angularFirestore.firestore;

      // ✅ Apply Firestore settings BEFORE enabling persistence AND before any reads happen
      firestore.settings({
        cacheSizeBytes: 10 * 1024 * 1024,
        experimentalForceLongPolling: true,
        ignoreUndefinedProperties: true,
      });
      try {
        await firestore.enablePersistence();
        this.indexedDBRecovery.resetRecoveryAttempts();
      } catch (err: any) {

        if (err.code === "failed-precondition") {
          // `failed-precondition` is an EXPECTED, recoverable condition (multiple tabs
          // open, or a Firestore SDK version change). The app degrades gracefully to
          // memory-only mode below and works normally, so we must NOT show the
          // "Page hiccup" stuck-site modal here. Previously, on a first-ever load the
          // empty `firebase_version` key made this fire a false-positive hiccup.
          localStorage.setItem("firebase_version", firebase.SDK_VERSION);
          console.warn("[FIREBASE] FAILED-PRECONDITION - Falling back to memory-only mode (no stuck dialog).");
          this.setupMemoryOnlyMode(angularFirestore);
        }

        else if (err.code === "unimplemented" || err.message?.toLowerCase().includes("indexeddb")) {
          console.warn("[FIREBASE] INDEXEDDB - Unsupported or blocked.");

          if (this.indexedDBRecovery.isPrivateBrowsingError(err)) {
            console.warn("[FIREBASE] PRIVATE-MODE - IndexedDB blocked due to private mode.");
            this.setupMemoryOnlyMode(angularFirestore);
            return;
          }

          if (this.indexedDBRecovery.isIndexedDBCorruptionError(err)) {
            await this.indexedDBRecovery.handleCorruptionAndReload();
            return;
          }

          console.warn("[FIREBASE] MODE-SWITCH - Using memory-only mode.");
          this.setupMemoryOnlyMode(angularFirestore);
        }
        else {
          if (this.indexedDBRecovery.isIndexedDBCorruptionError(err)) {
            console.error("[FIREBASE] CORRUPTION - Recovering...");
            await this.indexedDBRecovery.handleCorruptionAndReload();
            return;
          }

          this.setupMemoryOnlyMode(angularFirestore);
        }
      }

      throwIfAlreadyLoaded(parentModule, "CoreModule");

    } catch (e: any) {
      console.error("[FIREBASE] INIT ❌ Initialization error:", e);

      // Check for the specific iOS Safari createObjectStore race condition
      if (this.indexedDBRecovery.isCreateObjectStoreRaceCondition(e)) {
        this.setupMemoryOnlyMode(angularFirestore);
        throwIfAlreadyLoaded(parentModule, "CoreModule");
        return;
      }

      if (this.indexedDBRecovery.isPrivateBrowsingError(e)) {
        console.warn("[FIREBASE] PRIVATE-MODE - Using memory-only mode.");
        this.setupMemoryOnlyMode(angularFirestore);
        throwIfAlreadyLoaded(parentModule, "CoreModule");
        return;
      }

      if (this.indexedDBRecovery.isIndexedDBCorruptionError(e)) {
        console.error("[FIREBASE] CORRUPTION - Recovering...");
        this.indexedDBRecovery.handleCorruptionAndReload().catch((err) =>
          console.error("[FIREBASE] CORRUPTION ❌ Failed:", err)
        );
        return;
      }

      console.warn("[FIREBASE] MODE-SWITCH - Using memory-only mode.");
      try {
        this.setupMemoryOnlyMode(angularFirestore);
        throwIfAlreadyLoaded(parentModule, "CoreModule");
      } catch (setupError) {
        console.error("[FIREBASE] MODE-SWITCH ❌ Failed:", setupError);
      }
    }

  }

  private setupMemoryOnlyMode(angularFirestore: AngularFirestore): void {
    try {
      angularFirestore.firestore.settings({
        cacheSizeBytes: firebase.firestore.CACHE_SIZE_UNLIMITED,
        experimentalForceLongPolling: true,
        ignoreUndefinedProperties: true,
      });
    } catch (err: any) {
      if (err.message?.includes("settings can no longer be changed")) {
        console.warn("[FIREBASE] MEMORY-MODE - Settings already applied. Skipping.");
      } else {
        console.error("[FIREBASE] MEMORY-MODE ❌ Error:", err);
      }
    }
  }

  /**
   * Initialize Firebase Analytics with browser compatibility check
   * This prevents the "XHRInterface is not defined" error on incompatible WebViews
   */
  private async initializeAnalytics(firebaseApp: FirebaseApp): Promise<void> {
    try {
      // Check if Analytics is supported in the current browser/WebView environment
      const analyticsSupported = await isAnalyticsSupported();

      if (!analyticsSupported) {
        console.warn('[FIREBASE] Analytics is not supported in this browser environment. Analytics will be disabled.');
        return;
      }

      // Only initialize Analytics if supported
      const analytics = getAnalytics(firebaseApp);

    } catch (error) {
      // Gracefully handle Analytics initialization errors
      // This prevents the app from crashing if Analytics fails to initialize
      console.error('[FIREBASE] Failed to initialize Firebase Analytics:', error);

      // Check for the iOS Safari createObjectStore race condition
      if (this.indexedDBRecovery.isCreateObjectStoreRaceCondition(error)) {
        return;
      }

      // Check if this is the XHRInterface error
      if (error?.message?.includes('XHRInterface')) {
        return;
      }

      // Check for other IndexedDB-related errors
      if (this.indexedDBRecovery.isPrivateBrowsingError(error)) {
        return;
      }

      // Don't throw - let the app continue without Analytics
      console.warn('[FIREBASE] Continuing without Analytics due to initialization error.');
    }
  }
}
